// src/app/GeoGrid/hooks/useGeoGridFormSetup.ts (MODIFIED FOR UNIT CONVERSION)

import { useState, useEffect } from 'react'; // Removed useCallback
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../../reduxService/hooks';
import * as Yup from 'yup';
import * as ProjectApiService from '../../services/ProjectApiService'; // API to load project
import { IProject, ProjectData, IGeogridData } from '../../api/IApiResponse'; // Core types
import { unit as unitEnum, EProjectType } from '../../enum/project';
// Import GeoGrid config function specifically
import { getGeoGridJson, getGeogridProductsDataAsync } from '../../services/ProjectTypeService';
import { TGeogridProductData } from '../../data/IUnifiedProductData';
import { ValidationService, FieldConfig, ProjectTypeJson, GeoGridTypeJson } from '../../services/ValidationService';
import { convertGeoGridData } from '../../services/UnitConversionService'; // Assuming this is the correct path
import { fillParkingDeckDesignAxleLoad, mapExistingProjectToGeogridForm } from '../../services/GeoGridDataMapper';
import { setProject } from '../../Dynamic/store/project/projectslice';
import * as ProjectTypeService from '../../services/ProjectTypeService';
import { RootState } from '../../../reduxService';
import { logDebug, logError, logWarn, LogTag } from '../../utils/utils';
import { getErrorMessage } from '../../utils/typeGuards';
import { GeoGridFormData } from '../../types/FormTypes';

interface UseGeoGridFormSetupReturn {
    loading: boolean;
    errorDialog: string | null;
    groupedFields: Record<string, FieldConfig[]>;
    validationSchema: Yup.ObjectSchema<Record<string, unknown>> | null; // Yup typing is complex for nested objects
    validatedData: GeoGridFormData; // Initial Values for GeoGrid form
    projectUnitFromState: unitEnum; // Unit from the linked project
    isNewProject: boolean; // Indicates if GeoGrid DATA is new/default
    productData: TGeogridProductData | null;
    config: Record<string, unknown> | null; // Configuration object che include hiddenGroups
}

/**
 * Helper function to determine if GeoGrid data is effectively new
 */
export const isGeoGridDataNew = (geoGridData: IGeogridData | undefined): boolean => {
    if (!geoGridData || Object.keys(geoGridData).length === 0) {
        return true;
    }

    return !(
        Object.values(geoGridData.load || {}).some(v => v !== undefined && v !== null) ||
        Object.values(geoGridData.truck || {}).some(v => v !== undefined && v !== null) ||
        geoGridData.geogridBaseCourse?.geogrids?.length ||
        geoGridData.geogridBaseCourse?.thickness !== undefined ||
        geoGridData.geogridSubBaseCourse?.geogrids?.length ||
        geoGridData.geogridSubBaseCourse?.thickness !== undefined
    );
};

/**
 * Helper function to ensure GeoGrid data has the correct geogridType
 * Returns a new object with the correct type instead of modifying the input
 */
const ensureGeoGridType = (geoGridData: any, projectType: EProjectType): any => {
    // For standalone geogrid projects, preserve the type from saved data
    // Only enforce type for specific project types (container_yard, railways)
    const isStandaloneGeogrid = projectType === EProjectType.geogrid;

    // Initialize the type object if it doesn't exist
    if (!geoGridData.type) {
        // Determine default geogridType based on project type
        let defaultGeogridType: string;

        if (projectType === EProjectType.container_yard) {
            defaultGeogridType = 'Rectangular';
        } else if (projectType === EProjectType.railways) {
            defaultGeogridType = 'Railway';
        } else {
            // For standalone geogrid, default to Circular (but will be overwritten by saved data)
            defaultGeogridType = 'Circular';
        }

        logWarn('GeoGridSetup', 'type object is missing. Initializing with default geogridType', { defaultGeogridType, projectType }, LogTag.GEOGRID_CALC);
        const newGeoGridData = {
            ...geoGridData,
            type: { geogridType: defaultGeogridType },
        };
        logDebug('GeoGridSetup', 'type after initialization', newGeoGridData.type, LogTag.GEOGRID_CALC);
        return newGeoGridData;
    }

    // For railways and container_yard projects, always enforce the correct geogridType
    // For standalone geogrid projects, preserve the existing type from saved data
    const shouldForceUpdate = projectType === EProjectType.railways || projectType === EProjectType.container_yard;

    if (shouldForceUpdate && typeof geoGridData.type.geogridType !== 'undefined') {
        const correctGeogridType = projectType === EProjectType.railways ? 'Railway' : 'Rectangular';
        const currentType = geoGridData.type.geogridType;

        if (currentType !== correctGeogridType) {
            logWarn('GeoGridSetup', `Updating geogridType from '${currentType}' to '${correctGeogridType}' for project type '${projectType}'`, undefined, LogTag.GEOGRID_CALC);
            const newGeoGridData = {
                ...geoGridData,
                type: {
                    ...geoGridData.type,
                    geogridType: correctGeogridType,
                },
            };
            logDebug('GeoGridSetup', 'type after update', newGeoGridData.type, LogTag.GEOGRID_CALC);
            return newGeoGridData;
        }
    } else if (!isStandaloneGeogrid && typeof geoGridData.type.geogridType === 'undefined') {
        // Only set default if undefined and NOT standalone
        // Fix: Container Yard should use 'Rectangular', not 'Circular'
        const correctGeogridType = projectType === EProjectType.railways ? 'Railway' : projectType === EProjectType.container_yard ? 'Rectangular' : 'Circular';
        logWarn('GeoGridSetup', `Setting undefined geogridType to '${correctGeogridType}' for project type '${projectType}'`, undefined, LogTag.GEOGRID_CALC);
        const newGeoGridData = {
            ...geoGridData,
            type: {
                ...geoGridData.type,
                geogridType: correctGeogridType,
            },
        };
        return newGeoGridData;
    }

    // For standalone geogrid projects, preserve existing type from saved data
    if (isStandaloneGeogrid && geoGridData.type.geogridType) {
        logDebug('GeoGridSetup', `Preserving geogridType '${geoGridData.type.geogridType}' for standalone geogrid project`, undefined, LogTag.GEOGRID_CALC);
    }

    // No changes needed, return original object
    return geoGridData;
};

/**
 * Helper function to handle unit conversion with error handling
 */
const handleUnitConversion = (initialValues: Record<string, any>, config: ProjectTypeJson | GeoGridTypeJson, targetUnit: unitEnum, project?: IProject): Record<string, any> => {
    if (targetUnit !== unitEnum.Imperial) {
        return initialValues;
    }

    logDebug('GeoGridSetup', 'Converting initial values for Imperial display...', undefined, LogTag.UNIT_CONVERSION);

    try {
        // Extract conversion state from project if available
        const projectUnit = project?.unit;
        const conversionApplied = project?.conversionApplied;
        const originalUnit = project?.originalUnit;

        logDebug(
            'GeoGridSetup',
            'GeoGrid conversion state:',
            {
                targetUnit,
                projectUnit,
                conversionApplied,
                originalUnit,
                hasProject: !!project,
            },
            LogTag.UNIT_CONVERSION
        );

        // 🔧 FIX: For DISPLAY purposes, ALWAYS convert Metric→Imperial when in Imperial mode.
        // GeoGrid data from GeoGridDataMapper is ALWAYS in metric units (hardcoded values like
        // unitWeight=19 kN/m³, wheelRadius=0.15m, etc.), regardless of the project's conversionApplied state.
        // The `conversionApplied` flag tracks whether PROJECT data was converted for STORAGE,
        // NOT whether GeoGrid data should be converted for display.
        //
        // Previous bug: conversionApplied=true caused this function to skip conversion,
        // resulting in metric values displayed with Imperial units (e.g., 19 shown as "19 lbf/ft³").
        // See: useProjectFormSetupModular.ts lines 74-94 for the correct pattern.

        // ALWAYS convert from Metric to Imperial for display
        const convertedValues = convertGeoGridData(initialValues, config.fields, unitEnum.Imperial, unitEnum.SI, false, unitEnum.SI);

        if (convertedValues) {
            // Ensure critical properties aren't lost during conversion
            if (initialValues.type?.geogridType && (!convertedValues.type || !convertedValues.type.geogridType)) {
                logWarn('GeoGridSetup', 'Restoring type.geogridType lost during conversion', undefined, LogTag.UNIT_CONVERSION);
                if (!convertedValues.type) {
                    convertedValues.type = { ...initialValues.type };
                } else {
                    convertedValues.type.geogridType = initialValues.type.geogridType;
                }
            }
            return convertedValues;
        }
    } catch (conversionError) {
        logError('GeoGridSetup', 'Error during Imperial conversion:', conversionError, LogTag.UNIT_CONVERSION);
    }

    return initialValues; // Fallback to original values
};

export function useGeoGridFormSetup(): UseGeoGridFormSetupReturn {
    const { id: projectIdFromUrl } = useParams<{ id?: string }>();
    const dispatch = useAppDispatch();
    const currentReduxProject = useAppSelector(state => state.Dynamic.project?.data);
    const aashtoResults = useAppSelector(state => state.Dynamic.project.data?.aashtoResults);
    const output = useSelector((state: RootState) => state.Dynamic.results.data?.outputData);

    const [loading, setLoading] = useState<boolean>(true);
    const [errorDialog, setErrorDialog] = useState<string | null>(null);
    const [groupedFields, setGroupedFields] = useState<Record<string, FieldConfig[]>>({});
    const [validationSchema, setValidationSchema] = useState<Yup.ObjectSchema<any> | null>(null);
    const [initialGeoGridData, setInitialGeoGridData] = useState<Record<string, any>>({});
    const [projectUnit, setProjectUnit] = useState<unitEnum>(unitEnum.SI);
    const [isNewGeoGridData, setIsNewGeoGridData] = useState<boolean>(true);
    const [productData, setProductData] = useState<TGeogridProductData | null>(null);
    const [config, setConfig] = useState<any | null>(null);

    useEffect(() => {
        // FIXED: Skip re-initialization when returning from results with user modifications
        // This prevents form reset and preserves user input across navigation
        const hasUserModifications = currentReduxProject?.geogridUserModified;
        const hasExistingGeoGridData = currentReduxProject?.projectInfo?.data?.geoGrid && Object.keys(currentReduxProject.projectInfo.data.geoGrid).length > 0;
        const isReturningFromResults = hasUserModifications && hasExistingGeoGridData;

        if (isReturningFromResults && !loading && initialGeoGridData && Object.keys(initialGeoGridData).length > 0) {
            logDebug(
                'GeoGridSetup',
                'Skipping re-initialization - returning from results with user modifications',
                {
                    hasUserModifications,
                    hasExistingGeoGridData,
                    hasInitialData: Object.keys(initialGeoGridData).length > 0,
                },
                LogTag.FORM_SETUP
            );

            // FIXED: Ensure we use the current Redux geoGrid data when preserving user values
            const currentGeoGridData = currentReduxProject?.projectInfo?.data?.geoGrid;
            if (currentGeoGridData && JSON.stringify(currentGeoGridData) !== JSON.stringify(initialGeoGridData)) {
                logDebug('GeoGridSetup', 'Updating initial values with current Redux geoGrid data', currentGeoGridData, LogTag.FORM_SETUP);
                setInitialGeoGridData(currentGeoGridData);
            }
            return;
        }

        const setupGeoGridForm = async () => {
            setLoading(true);
            setErrorDialog(null);

            let projectDataSource: IProject | null = null;
            let projectDataForForm: ProjectData | undefined;

            try {
                // --- Step 1: Determine Project Context ---
                logDebug('GeoGridSetup', `Determining data source. URL ID: ${projectIdFromUrl}`, undefined, LogTag.FORM_SETUP);

                if (!projectIdFromUrl) {
                    throw new Error('Project ID is missing in the URL.');
                }

                if (currentReduxProject && currentReduxProject.guid === projectIdFromUrl) {
                    logDebug('GeoGridSetup', 'Using current Redux project data', undefined, LogTag.FORM_SETUP);
                    projectDataSource = currentReduxProject;
                } else {
                    logDebug('GeoGridSetup', `Loading project ${projectIdFromUrl} from API`, undefined, LogTag.API_CALLS);
                    // Reason: Use ProjectApiService.loadProjectByGuid which calls /api/Project/projects/{guid}
                    const loadResult = await ProjectApiService.loadProjectByGuid(projectIdFromUrl);

                    if (!loadResult.success || !loadResult.data) {
                        throw new Error(`Project ${projectIdFromUrl} not found via API: ${loadResult.error}`);
                    }

                    // Transform backend project to frontend format
                    const backendProject = loadResult.data;
                    projectDataSource = ProjectApiService.transformProjectFromBackend(backendProject);

                    projectDataSource.newProject = false;

                    // IMPORTANT: transformProjectFromBackend already determines type correctly (authoritative source)
                    // We only need to determine method if it's not already set
                    // For geogrid standalone projects: type='geogrid', method='' (no method needed)
                    if (!projectDataSource.method) {
                        projectDataSource.method = ProjectTypeService.getMethodByProjectData(projectDataSource.projectInfo.data);
                        logDebug('GeoGridSetup', 'Method determined from project data', { method: projectDataSource.method }, LogTag.API_CALLS);
                    } else {
                        logDebug('GeoGridSetup', 'Using saved method from backend', { method: projectDataSource.method }, LogTag.API_CALLS);
                    }

                    logDebug('GeoGridSetup', 'Dispatching setProject with fetched data', {
                        type: projectDataSource.type,
                        method: projectDataSource.method,
                        hasGeoGrid: !!(projectDataSource.projectInfo.data as any).geoGrid
                    }, LogTag.API_CALLS);
                    dispatch(setProject(projectDataSource));
                }

                if (!projectDataSource?.projectInfo?.data) {
                    throw new Error('Failed to determine project data source or projectInfo.data is missing.');
                }

                // Deep clone projectDataForForm to prevent immutability violations
                // This ensures nested objects can be safely modified without affecting Redux state
                projectDataForForm = JSON.parse(JSON.stringify(projectDataSource.projectInfo.data)) as ProjectData;
                logDebug('GeoGridSetup', 'Created deep clone of projectDataForForm to ensure mutability', undefined, LogTag.FORM_SETUP);

                // --- Step 2: Apply Project-Specific Data Mapping ---
                logDebug('GeoGridSetup', 'Applying project-specific data mapping...', undefined, LogTag.FORM_SETUP);

                // Check if there's existing geogrid FORM data (not just Railway's selectedGeogrid)
                // Railway projects have geoGrid.selectedGeogrid, but that's NOT geogrid form data
                // Geogrid form data has geogridBaseCourse, geogridSubBaseCourse, load, truck, etc.
                const hasGeogridFormData = !!projectDataForForm.geoGrid && (
                    !!projectDataForForm.geoGrid.geogridBaseCourse ||
                    !!projectDataForForm.geoGrid.geogridSubBaseCourse ||
                    !!projectDataForForm.geoGrid.load ||
                    !!projectDataForForm.geoGrid.truck
                );
                const hasExistingGeoGridData = hasGeogridFormData;
                const hasGeoGridResults = !!projectDataSource.geogridHasBeenRan; // Check if geogrid has been run before
                const hasUserModifications = !!projectDataSource.geogridUserModified; // Check if user has manually edited the form

                // Preserve user values if we have existing data and either:
                // 1. User has been to results before (hasGeoGridResults), OR
                // 2. User has manually modified the form (hasUserModifications)
                const shouldPreserveUserValues = hasExistingGeoGridData && (hasGeoGridResults || hasUserModifications);

                logDebug(
                    'GeoGridSetup',
                    'Mapping context:',
                    {
                        hasExistingGeoGridData,
                        hasGeoGridResults,
                        hasUserModifications,
                        shouldPreserveUserValues,
                        geogridHasBeenRan: projectDataSource.geogridHasBeenRan,
                        geogridUserModified: projectDataSource.geogridUserModified,
                    },
                    LogTag.FORM_SETUP
                );

                // Only apply automatic mapping if we should NOT preserve user values
                if (!shouldPreserveUserValues) {
                    logDebug('GeoGridSetup', 'Automatic mapping will be applied after config is loaded.', undefined, LogTag.FORM_SETUP);
                } else {
                    logDebug('GeoGridSetup', 'Preserving existing geogrid data, skipping automatic mapping.', undefined, LogTag.FORM_SETUP);
                    // Ensure we have the geoGrid object even when preserving
                    if (!projectDataForForm.geoGrid) {
                        projectDataForForm.geoGrid = {};
                    }
                    projectDataForForm.geoGrid = fillParkingDeckDesignAxleLoad(projectDataForForm, projectDataForForm.geoGrid);
                }

                // Ensure required GeoGrid properties
                if (projectDataForForm.geoGrid) {
                    const projectType = projectDataSource.type || EProjectType.geogrid; // Default fallback
                    projectDataForForm.geoGrid = ensureGeoGridType(projectDataForForm.geoGrid, projectType);
                } else {
                    const projectType = projectDataSource.type || EProjectType.geogrid; // Default fallback
                    let defaultGeogridType: string;

                    if (projectType === EProjectType.container_yard) {
                        defaultGeogridType = 'Rectangular';
                    } else if (projectType === EProjectType.railways) {
                        defaultGeogridType = 'Railway';
                    } else {
                        defaultGeogridType = 'Circular';
                    }

                    projectDataForForm.geoGrid = { type: { geogridType: defaultGeogridType } };
                }

                // --- Step 3: Load Configuration and Product Data ---
                logDebug('GeoGridSetup', 'Loading GeoGrid config and Product Data...', undefined, LogTag.API_CALLS);

                // Determine if we're in standalone mode (direct geogrid creation without a specific project type)
                // Standalone mode: projectType is geogrid or project doesn't have a specific type/method
                const projectType = projectDataSource?.type || EProjectType.geogrid;
                const projectMethod = projectDataSource?.method;
                const isStandaloneMode = projectType === EProjectType.geogrid || !projectMethod;

                logDebug('GeoGridSetup', `Determining standalone mode: projectType=${projectType}, isStandalone=${isStandaloneMode}`, undefined, LogTag.FORM_SETUP);

                // Get country code and project type for geogrid product filtering
                const productCountry = projectDataSource?.projectInfo?.productCountry || undefined;
                // Filter geogrid products by project type (exclude 'geogrid' standalone type)
                const filterProjectType = projectType !== EProjectType.geogrid ? (projectType as 'airport' | 'paved' | 'unpaved' | 'railways' | 'container_yard' | 'parking_deck') : undefined;
                logDebug('GeoGridSetup', `Product filtering - Country: ${productCountry || 'not set'}, Type: ${filterProjectType || 'all'}`, undefined, LogTag.FORM_SETUP);

                const [config, products] = await Promise.all([getGeoGridJson(isStandaloneMode, projectType), getGeogridProductsDataAsync(productCountry, filterProjectType)]);

                if (!config?.fields) throw new Error('Invalid GeoGrid configuration.');
                if (!products) throw new Error('Failed to load GeoGrid product data.');

                setGroupedFields(config.fields);
                setProductData(products);
                setConfig(config); // Salva la configurazione completa

                // --- Step 3.5: Apply Project-Specific Data Mapping (after config is loaded) ---
                // Only apply automatic mapping if we should NOT preserve user values
                if (!shouldPreserveUserValues) {
                    logDebug('GeoGridSetup', 'Applying automatic mapping from project data.', undefined, LogTag.FORM_SETUP);
                    logDebug(
                        'GeoGridSetup',
                        'projectDataForForm is cloned and mutable:',
                        {
                            hasGeoGrid: !!projectDataForForm.geoGrid,
                            isExtensible: Object.isExtensible(projectDataForForm),
                            geoGridExtensible: projectDataForForm.geoGrid ? Object.isExtensible(projectDataForForm.geoGrid) : 'N/A',
                        },
                        LogTag.FORM_SETUP
                    );

                    // Get calculated design thickness for paved projects
                    const calculatedDesignThickness = (projectDataForForm as any)?.calculatedDesignThickness;

                    const mappedGeoGridData = mapExistingProjectToGeogridForm(
                        projectDataForForm,
                        output, // outputData
                        aashtoResults, // aashtoResults
                        calculatedDesignThickness, // calculatedDesignThickness
                        projectDataSource.unit || unitEnum.SI, // targetUnit
                        config.fields, // configFields
                        projectDataSource, // project with conversion state
                        false // preserveUserValues
                    );

                    if (mappedGeoGridData) {
                        try {
                            projectDataForForm.geoGrid = mappedGeoGridData;
                            logDebug('GeoGridSetup', 'Successfully assigned mapped geoGrid data to cloned projectDataForForm', undefined, LogTag.FORM_SETUP);
                            logDebug('GeoGridSetup', 'Mapped geoGrid load values:', mappedGeoGridData.load, LogTag.GEOGRID_CALC);
                        } catch (assignmentError) {
                            logError('GeoGridSetup', 'Error assigning geoGrid data to cloned object:', assignmentError, LogTag.FORM_SETUP);
                            throw new Error('Failed to assign mapped geoGrid data - object mutability issue');
                        }
                    } else if (!projectDataForForm.geoGrid) {
                        projectDataForForm.geoGrid = {};
                        logDebug('GeoGridSetup', 'Initialized empty geoGrid object', undefined, LogTag.FORM_SETUP);
                    }
                }

                // --- Step 4: Generate Validation Schema ---
                const unit = projectDataSource.unit || unitEnum.SI;
                setProjectUnit(unit);
                logDebug('GeoGridSetup', `Generating schema for unit: ${unit}`, undefined, LogTag.FORM_SETUP);

                const schema = ValidationService.generateGeoGridValidationSchema(config, unit);
                setValidationSchema(schema);

                // --- Step 5: Prepare Initial Values ---
                logDebug('GeoGridSetup', 'Building initial values...', undefined, LogTag.FORM_SETUP);
                const existingGeoGridData = projectDataForForm.geoGrid as IGeogridData | undefined;

                // DEBUG: Log what we received from mapping
                logDebug('GeoGridSetup', 'existingGeoGridData after mapping:', existingGeoGridData, LogTag.GEOGRID_CALC);
                logDebug('GeoGridSetup', 'existingGeoGridData.load after mapping:', existingGeoGridData?.load, LogTag.GEOGRID_CALC);

                const isNewData = isGeoGridDataNew(existingGeoGridData);

                setIsNewGeoGridData(isNewData);
                logDebug('GeoGridSetup', `Is new GeoGrid data: ${isNewData}`, undefined, LogTag.FORM_SETUP);

                const initialValuesSI = ValidationService.buildInitialGeoGridValues(config, existingGeoGridData, isNewData);

                // Log values before and after handleUnitConversion to track double conversion
                logDebug(
                    'GeoGridSetup',
                    'Values before handleUnitConversion (should be metric):',
                    {
                        unit,
                        tirePressure: initialValuesSI?.truckLoadForFirstLift?.tirePressure,
                        uniformLoad: initialValuesSI?.load?.uniformLoad,
                        deckHalfWidth: initialValuesSI?.load?.deckHalfWidth,
                    },
                    LogTag.UNIT_CONVERSION
                );

                // Apply unit conversion only once here (data from mapping is always metric)
                const finalInitialValues = handleUnitConversion(initialValuesSI, config, unit, projectDataSource);

                logDebug(
                    'GeoGridSetup',
                    'Final values after handleUnitConversion:',
                    {
                        finalTirePressure: finalInitialValues?.truckLoadForFirstLift?.tirePressure,
                        finalUniformLoad: finalInitialValues?.load?.uniformLoad,
                        finalDeckHalfWidth: finalInitialValues?.load?.deckHalfWidth,
                    },
                    LogTag.UNIT_CONVERSION
                );

                // LOG FINALE per debug: verifica che geogridType sia corretto
                logDebug('GeoGridSetup', 'Setting final initial values with geogridType:', { geogridType: finalInitialValues?.type?.geogridType, projectType: projectDataSource?.type }, LogTag.GEOGRID_CALC);

                setInitialGeoGridData(finalInitialValues);
            } catch (err: unknown) {
                logError('GeoGridSetup', 'Error:', err, LogTag.CRITICAL);
                setErrorDialog(getErrorMessage(err) || 'Failed to setup GeoGrid form.');

                // Reset states on error
                setGroupedFields({});
                setValidationSchema(null);
                setInitialGeoGridData({});
                setProjectUnit(unitEnum.SI);
                setIsNewGeoGridData(true);
                setProductData(null);
            } finally {
                setLoading(false);
            }
        };

        setupGeoGridForm();
    }, [
        projectIdFromUrl,
        dispatch,
        currentReduxProject?.projectInfo, // Only depend on projectInfo, not the entire project (excludes geogridUserModified)

        aashtoResults,
        output,
    ]); // Fixed: Exclude geogridUserModified from dependencies to prevent re-initialization on first input change

    return {
        loading: loading,
        errorDialog: errorDialog,
        groupedFields: groupedFields,
        validationSchema: validationSchema,
        validatedData: initialGeoGridData,
        projectUnitFromState: projectUnit,
        isNewProject: isNewGeoGridData,
        productData: productData,
        config: config,
    };
}
